import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import FeatureComparisonTable from '../components/FeatureComparisonTable';
import EnterpriseEnquiryDialog from '../components/EnterpriseEnquiryDialog';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import {
  TRIAL_DAYS,
  displayPlansForCurrency,
  formatPlanPrice,
} from '../lib/pricingCatalog';

const LOGO_FULL = '/images/logo-full.png';

function PlanCard({ title, price, subtitle, features, cta, onClick, badge }) {
  return (
    <Card className="border-zinc-200 shadow-sm hover:shadow-md transition-shadow h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-heading text-zinc-900">{title}</CardTitle>
          {badge ? (
            <span className="px-2 py-1 text-xs rounded-full bg-brand-50 text-brand-700 border border-brand-200">
              {badge}
            </span>
          ) : null}
        </div>
        <p className="text-zinc-500 text-sm">{subtitle}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-heading font-semibold text-zinc-900">{price}</span>
        </div>
        <ul className="space-y-2 text-sm text-zinc-600">
          {(features || []).map((f) => (
            <li key={f} className="flex gap-2">
              <span className="mt-1 h-2 w-2 rounded-full bg-zinc-300" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <Button onClick={onClick} className="w-full bg-brand-600 hover:bg-brand-700 text-white">{cta}</Button>
      </CardContent>
    </Card>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const currency = 'INR';
  const [apiPlans, setApiPlans] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.publicPlans(currency);
        if (!cancelled) setApiPlans(res.data?.plans || []);
      } catch {
        if (!cancelled) setApiPlans([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const plans = displayPlansForCurrency(currency, apiPlans);
  const trial = plans.find((p) => p.slug === 'trial');
  const enterprise = plans.find((p) => p.slug === 'enterprise');

  const reg = (plan, cycle) => {
    if (plan === 'enterprise') {
      setEnquiryOpen(true);
      return;
    }
    navigate(`/register/company?plan=${plan}&cycle=${cycle}&currency=${currency}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between gap-6">
          <img src={LOGO_FULL} alt="OMNICHEK" className="h-10 object-contain" />
          <div className="flex items-center gap-3">
            {user && user !== false ? (
              <>
                <Button variant="outline" onClick={() => navigate('/dashboard')}>Dashboard</Button>
                <Button variant="ghost" onClick={async () => { await logout(); navigate('/login'); }}>
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate('/login')}>Sign in</Button>
                <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => navigate(`/register/company?currency=${currency}`)}>
                  Start free trial
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-10">
          <h1 className="text-3xl font-heading font-semibold text-zinc-900">Simple pricing</h1>
          <p className="mt-2 text-zinc-500">
            Start with a {TRIAL_DAYS}-day free trial. Production use is Enterprise — contact sales for a quote.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
          <PlanCard
            title="Free trial"
            badge={`${TRIAL_DAYS} days`}
            price={formatPlanPrice(0, currency)}
            subtitle={`Try Omnichek with full access for ${TRIAL_DAYS} days.`}
            features={trial?.features?.length ? trial.features : [
              `${TRIAL_DAYS}-day trial`,
              'Consumer verify portal',
              'Basic analytics',
            ]}
            cta="Start free trial"
            onClick={() => reg('free', 'trial')}
          />
          <PlanCard
            title="Enterprise"
            price="Custom"
            subtitle="On-site / private cloud, SLA, and unlimited volume"
            features={enterprise?.features || []}
            cta="Contact sales"
            onClick={() => reg('enterprise', 'custom')}
          />
        </div>

        <FeatureComparisonTable currency={currency} apiPlans={apiPlans} />
        <EnterpriseEnquiryDialog open={enquiryOpen} onOpenChange={setEnquiryOpen} />
      </div>
    </div>
  );
}
